"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ComponentProps, MouseEvent } from "react";
import { modeForPath, worldBus } from "@/lib/world-bus";

// Transición entre páginas (guía 6 y 7.5): el contenido se desvanece rápido, el mundo
// de atrás nunca se corta y la cámara empieza a viajar en el mismo instante. Menos de
// 1,2 s en total, interrumpible (otro clic retoma desde donde está) y sin bloquear: si
// algo falla, el enlace sigue siendo un <a> normal.

const LEAVE_MS = 170;
let pending: number | null = null;

export function navigateWithTransition(push: (href: string) => void, href: string) {
  const root = document.documentElement;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (pending !== null) window.clearTimeout(pending);
  root.dataset.transition = "leaving";
  // La cámara arranca ya, sin esperar a que cargue la página nueva.
  const path = new URL(href, window.location.href).pathname;
  worldBus.setMode(modeForPath(path));
  if (path === "/") worldBus.u = 0;
  pending = window.setTimeout(
    () => {
      pending = null;
      push(href);
    },
    reduce ? 60 : LEAVE_MS,
  );
}

type Props = ComponentProps<typeof Link>;

export function TransitionLink({ href, onClick, ...rest }: Props) {
  const router = useRouter();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (rest.target && rest.target !== "_self") return;
    const url = new URL(typeof href === "string" ? href : String(href.pathname ?? "/"), window.location.href);
    if (url.origin !== window.location.origin) return;
    // Mismo documento: anclas y recargas de la misma ruta siguen el camino normal.
    if (url.pathname === window.location.pathname) return;
    e.preventDefault();
    navigateWithTransition((h) => router.push(h), url.pathname + url.search + url.hash);
  };
  return <Link href={href} onClick={handle} {...rest} />;
}
